import { useState } from "react";
import { ArrowRight } from "lucide-react";
import Reveal from "../components/Reveal";

interface Article { cat: string; title: string; author: string; date: string; read: string; href: string; }

const FEATURED = {
  cat: "Defence-Ready",
  read: "8 min read",
  date: "May 2026",
  author: "Defence Practice",
  title: "The seven gaps we find in every defence-readiness audit, and how to close them in 30 days",
  excerpt: "A field-tested playbook from the audits we run for AU/NZ companies supplying defence, government and critical-infrastructure customers.",
  href: "/blog/posts/defence-readiness-audit-gaps.html",
};

const ARTICLES: Article[] = [
  { cat: "AI Strategy",    title: "Why AI-native beats AI-bolted-on in industrial software", author: "Paisums Editorial", date: "May 2026", read: "6 min", href: "/blog/posts/ai-native-vs-bolt-on.html" },
  { cat: "Mining",         title: "From telematics to copilots: predictive maintenance for Pilbara fleets", author: "Industry · Mining", date: "Apr 2026", read: "5 min", href: "/blog/posts/pilbara-predictive-maintenance.html" },
  { cat: "GIS",            title: "AI change-detection at national scale: lessons from a federal pilot", author: "GIS Practice", date: "Apr 2026", read: "7 min", href: "/blog/posts/ai-change-detection-geospatial.html" },
  { cat: "Defence-Ready",  title: "Generating defence policy with AI: what the AUKUS regulators actually want", author: "Defence Practice", date: "Mar 2026", read: "9 min", href: "/blog/posts/aukus-policy-generation.html" },
  { cat: "Machinery",      title: "Sensor fusion for heavy machinery: a 12-week path to anomaly detection", author: "Industry · Machinery", date: "Mar 2026", read: "6 min", href: "/blog/posts/sensor-fusion-heavy-machinery.html" },
  { cat: "Engineering",    title: "Senior squads, not body shops: how we structure AI delivery teams", author: "Engineering", date: "Feb 2026", read: "4 min", href: "/blog/posts/ai-delivery-team-structure.html" },
  { cat: "Data Analytics", title: "LLM-powered analyst copilots that actually move metrics", author: "Data Practice", date: "Feb 2026", read: "6 min", href: "/blog/posts/wa-mining-sovereign-software.html" },
  { cat: "Insights",       title: "WA mining\u2019s next decade: AI, autonomy and the case for sovereign software", author: "Paisums Editorial", date: "Jan 2026", read: "8 min", href: "/blog/posts/wa-mining-sovereign-software.html" },
];

const CATS = ["All", "AI Strategy", "Defence-Ready", "Mining", "Machinery", "GIS", "Data Analytics", "Engineering", "Insights"];

const GRADIENT_BG = (i: number): string =>
  i % 4 === 0
    ? "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.16), transparent 55%), repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 10px, transparent 10px 20px)"
    : i % 4 === 1
      ? "radial-gradient(circle at 70% 70%, rgba(255,255,255,0.18), transparent 55%), repeating-linear-gradient(-45deg, rgba(255,255,255,0.04) 0 10px, transparent 10px 20px)"
      : i % 4 === 2
        ? "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.14), transparent 60%), repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0 10px, transparent 10px 20px)"
        : "radial-gradient(circle at 20% 80%, rgba(255,255,255,0.16), transparent 55%), repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 10px, transparent 10px 20px)";

function FeaturedArticle() {
  return (
    <Reveal>
      <a href={FEATURED.href} className="block">
      <article className="liquid-glass rounded-3xl overflow-hidden group grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] hover:bg-white/[0.01] transition-colors">
        <div className="relative aspect-[16/10] lg:aspect-auto lg:min-h-[420px] overflow-hidden order-2 lg:order-1 glow-drift">
          <video
            src="/videos/flowers.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-transparent" />
          <div className="absolute top-4 left-4 liquid-glass rounded-full px-3 py-1 text-white text-[10px] tracking-widest uppercase z-10">Featured</div>
        </div>
        <div className="p-8 md:p-10 flex flex-col order-1 lg:order-2">
          <div className="text-white/40 text-[10px] tracking-widest uppercase">{FEATURED.cat} · {FEATURED.date}</div>
          <h2 className="font-instrument text-3xl md:text-4xl text-white tracking-tight mt-4 leading-[1.15]">{FEATURED.title}</h2>
          <p className="text-white/60 text-base leading-relaxed mt-5">{FEATURED.excerpt}</p>
          <div className="mt-auto pt-8 flex items-center justify-between">
            <div className="text-white/40 text-xs tracking-widest uppercase">{FEATURED.author} · {FEATURED.read}</div>
            <span className="liquid-glass rounded-full px-5 py-2 text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-white/5 transition-colors">
              Read article <ArrowRight size={14} />
            </span>
          </div>
        </div>
      </article>
      </a>
    </Reveal>
  );
}

function ArticleCard({ a, i }: { a: Article; i: number }) {
  return (
    <Reveal delay={i * 80}>
      <a href={a.href} className="block h-full">
        <article className="liquid-glass rounded-3xl overflow-hidden h-full flex flex-col group hover:bg-white/[0.02] transition-colors">
          <div className="aspect-[16/10] relative overflow-hidden">
            <div className="absolute inset-0" style={{ background: GRADIENT_BG(i) }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute top-4 left-4 liquid-glass rounded-full px-3 py-1 text-white text-[10px] tracking-widest uppercase">{a.cat}</div>
          </div>
          <div className="p-6 md:p-7 flex flex-col flex-1">
            <h3 className="font-instrument text-white text-xl md:text-2xl tracking-tight leading-[1.2]">{a.title}</h3>
            <div className="mt-auto pt-6 flex items-center justify-between text-white/40 text-[10px] tracking-widest uppercase">
              <span>{a.author}</span>
              <span>{a.date} · {a.read}</span>
            </div>
          </div>
        </article>
      </a>
    </Reveal>
  );
}

export default function BlogListing() {
  const [cat, setCat] = useState("All");
  const filtered = cat === "All" ? ARTICLES : ARTICLES.filter((a) => a.cat === cat);
  return (
    <section className="bg-black px-6 py-20 md:py-28">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div className="text-white/40 text-sm tracking-widest uppercase">Latest</div>
          <h2 className="font-instrument text-4xl md:text-6xl text-white tracking-tight mt-4 leading-[1.1]">
            What we've been <em className="italic-serif text-white/50">writing</em>.
          </h2>
        </Reveal>

        <div className="mt-10 mb-12">
          <FeaturedArticle />
        </div>

        <Reveal>
          <div className="flex flex-wrap gap-2 mb-8">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)}
                      className={`liquid-glass rounded-full px-4 py-1.5 text-xs tracking-widest uppercase transition-colors ${cat === c ? "text-black" : "text-white/70 hover:text-white"}`}
                      style={cat === c ? { background: "#fff" } : undefined}>
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((a, i) => <ArticleCard key={a.title} a={a} i={i} />)}
        </div>
      </div>
    </section>
  );
}
